import type { FC } from 'react';
import { FeatureIndicatorIcon } from '@components/FeatureIndicatorIcon';
import LoadingOverlay from '@components/LoadingOverlay';
import { Show } from '@components/Show';
import { handleReplaceMentionValues, useMentionValues } from '@ContractBuilder/modules/block';
import { blockViewBodyClasses, indicatorIconsClasses } from '@ContractBuilder/modules/block/classes';
import { BlockContent } from '@ContractBuilder/modules/block-content';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import type { EntityData } from '@domain/types/entity';
import { mdiPaletteSwatchVariant } from '@mdi/js';
import { useBlockLibrary } from '@queries/fetchBlocks';
import { asResourceBlock } from '@root/helpers/blocks/as-resource-block';
import { isTemplatePath } from '@utils/app-paths';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

interface BlockPreviewProps {
  id: string;
  submission?: EntityData;
  endorsementParent?: EntityData;
}

export const BlockPreview: FC<BlockPreviewProps> = ({ id, submission, endorsementParent }) => {
  const { data: block, isFetching } = useBlockLibrary(id);
  const { variations, usedInTemplates } = block || {};
  const isTemplate = isTemplatePath();
  const variationsCount = variations?.length ?? 0;
  const showUsedInTemplatesControls = isTemplate && usedInTemplates;
  const showIcons = variationsCount || showUsedInTemplatesControls;

  const blockToUse: ResourceBlock = block ? asResourceBlock(block) : ({} as ResourceBlock);
  const mentionValues = useMentionValues({ block: blockToUse, submission, endorsementParent });
  const blockContent = handleReplaceMentionValues(blockToUse, mentionValues);

  const blockClasses = twMerge(
    blockViewBodyClasses({
      pdf: true,
      disabled: true,
      highlighted: false,
      className: ['p-0!', '-mx-5', 'w-full!', 'min-w-0!'],
    }),
  );

  return (
    <>
      <LoadingOverlay active={isFetching} />
      <Show when={!!block}>
        <div className="cb-wysiwyg group prose-sm pointer-events-none relative cursor-default! border-b bg-white py-4">
          <div className={twMerge('flex flex-col')}>
            <div className={blockClasses}>
              <BlockContent
                block={{ ...blockToUse, content: blockContent ?? '' } as ResourceBlock}
                isVisible
                isPdfRender
                mentionValues={mentionValues}
              />
              {showIcons && (
                <div className={clsx(indicatorIconsClasses)}>
                  {!!variationsCount && (
                    <FeatureIndicatorIcon
                      path={mdiPaletteSwatchVariant}
                      content={`${variationsCount} variations`}
                      data-testid="block-icon-indicator:variations"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Show>
    </>
  );
};
